import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Fade,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  AccessTime,
  PlayArrow,
  Visibility,
  Schedule,
} from '@mui/icons-material';

import PropTypes from 'prop-types';
import LogsViewer from '../../workflows/LogsViewer';

// Utility functions to format dates and times
const formatDate = (dt) => {
  if (!dt) return '-';
  const date = new Date(dt);
  return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
};

const formatTime = (dt) => {
  if (!dt) return '-';
  const date = new Date(dt);
  return date.toISOString().split('T')[1].split('.')[0]; // Returns HH:MM:SS
};

const truncateId = (id) => {
  if (!id) return '-';
  return id.length > 12 ? `${id.substring(0, 12)}...` : id;
};

// Status styling functions with theme colors
const getStatusStyles = (status) => {
  switch (status?.toUpperCase()) {
    case 'SUCCESS':
      return {
        label: 'Success',
        color: '#2e7d32',
        bgColor: '#e8f5e9',
        icon: <CheckCircle fontSize="small" />,
      };
    case 'FAILED':
      return {
        label: 'Failed',
        color: '#d32f2f',
        bgColor: '#ffebee',
        icon: <Error fontSize="small" />,
      };
    case 'ACTIVE':
    case 'PROCESSING':
      return {
        label: 'Processing',
        color: '#0056D2',
        bgColor: '#e3f2fd',
        icon: <PlayArrow fontSize="small" />,
      };
    case 'PARTIAL_SUCCESS':
      return {
        label: 'Partial',
        color: '#ed6c02',
        bgColor: '#fff3e0',
        icon: <Error fontSize="small" />,
      };
    default:
      return {
        label: 'Unknown',
        color: '#757575',
        bgColor: '#f5f5f5',
        icon: <AccessTime fontSize="small" />,
      };
  }
};

const getRunModeStyles = (mode = 'ON_DEMAND') => {
  switch (mode?.toUpperCase()) {
    case 'ON_DEMAND':
      return {
        label: 'Manual',
        color: '#0056D2',
        bgColor: '#e3f2fd',
        icon: <PlayArrow fontSize="small" />,
      };
    case 'AUTO_SCHEDULE':
      return {
        label: 'Scheduled',
        color: '#ed6c02',
        bgColor: '#fff3e0',
        icon: <Schedule fontSize="small" />,
      };
    default:
      return {
        label: 'Unknown Mode',
        color: '#757575',
        bgColor: '#f5f5f5',
        icon: <Error fontSize="small" />,
      };
  }
};

const WorkflowRunDialog = ({ open, onClose, data, loading }) => {
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedWorkflowRunId, setSelectedWorkflowRunId] = useState(null);

  const handleViewLogs = (workflowRunId) => {
    setSelectedWorkflowRunId(workflowRunId);
    setLogsDialogOpen(true);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={300}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(10, 18, 30, 0.35)',
          backdropFilter: 'blur(4px)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          background: '#ffffff',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.15)',
          border: '1px solid #E8E8EE',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #E8E8EE',
          background: '#F9F9FB',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: '#000000',
            fontSize: '1.125rem',
          }}
        >
          Workflow Run History
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          overflowX: 'hidden',
          overflowY: 'auto',
          maxHeight: '65vh',
          p: 3,
          background: '#ffffff',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#0056D2' }} />
          </Box>
        ) : data.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 8,
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography
              sx={{
                color: '#4a4a4a',
                fontSize: '0.95rem',
                textAlign: 'center',
              }}
            >
              No workflow runs found for this workflow.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: '8px',
                border: '1px solid #E8E8EE',
                boxShadow: 'none',
                overflow: 'hidden',
              }}
            >
              <Table size="small">
                <TableHead
                  sx={{
                    background: '#F9F9FB',
                    '& th': {
                      color: '#000000',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      borderBottom: '2px solid #E8E8EE',
                    },
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ py: 2 }}>Run ID</TableCell>
                    <TableCell sx={{ py: 2 }}>Status</TableCell>
                    <TableCell sx={{ py: 2 }}>Mode</TableCell>
                    <TableCell sx={{ py: 2 }}>Date</TableCell>
                    <TableCell sx={{ py: 2 }}>Start Time</TableCell>
                    <TableCell sx={{ py: 2 }}>End Time</TableCell>
                    <TableCell sx={{ py: 2, textAlign: 'center' }}>Success</TableCell>
                    <TableCell sx={{ py: 2, textAlign: 'center' }}>Failed</TableCell>
                    <TableCell sx={{ py: 2, textAlign: 'center' }}>View Logs</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {data.map((run, index) => {
                   const statusStyle = getStatusStyles(run.status);
                   const runModeValue = run.is_scheduled ? 'AUTO_SCHEDULE' : 'ON_DEMAND';
                   const runModeStyle = getRunModeStyles(runModeValue);
                   const runId = truncateId(run.workflow_run_id);
                   const startDate = formatDate(run.start_time);
                   const startTime = formatTime(run.start_time);
                   const endTime = formatTime(run.end_time);
                   const successCount =
                     typeof run.success_record_count === 'number'
                       ? run.success_record_count
                       : '';
                   const failedCount =
                     typeof run.failure_record_count === 'number'
                       ? run.failure_record_count
                       : '';
                   const errorMessage = run.error_message;

                   return (
                     <React.Fragment key={index}>
                       <TableRow
                         hover
                         sx={{
                           '&:hover': {
                             backgroundColor: '#F9F9FB',
                           },
                           '& td': {
                             borderBottom: '1px solid #E8E8EE',
                           },
                         }}
                       >
                        {/* Run ID - Truncated */}
                        <TableCell
                          sx={{
                            fontSize: '0.875rem',
                            whiteSpace: 'nowrap',
                            color: '#4a4a4a',
                            py: 1.5,
                          }}
                        >
                          <Tooltip title={run.workflow_run_id} placement="top">
                            <span>{runId}</span>
                          </Tooltip>
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            icon={statusStyle?.icon}
                            label={statusStyle?.label}
                            size="small"
                            sx={{
                              backgroundColor: statusStyle?.bgColor,
                              color: statusStyle?.color,
                              fontWeight: 500,
                              height: 28,
                              borderRadius: '8px',
                              border: 'none',
                              '& .MuiChip-icon': {
                                color: statusStyle?.color,
                                ml: 0.5,
                              },
                              '& .MuiChip-label': {
                                px: 1,
                                fontSize: '0.813rem',
                              },
                            }}
                          />
                        </TableCell>

                        {/* Mode */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            icon={runModeStyle?.icon}
                            label={runModeStyle?.label}
                            size="small"
                            sx={{
                              backgroundColor: runModeStyle?.bgColor,
                              color: runModeStyle?.color,
                              fontWeight: 500,
                              height: 28,
                              borderRadius: '8px',
                              border: 'none',
                              '& .MuiChip-icon': {
                                color: runModeStyle?.color,
                                ml: 0.5,
                              },
                              '& .MuiChip-label': {
                                px: 1,
                                fontSize: '0.813rem',
                              },
                            }}
                          />
                        </TableCell>

                        {/* Date */}
                        <TableCell
                          sx={{
                            fontSize: '0.875rem',
                            color: '#4a4a4a',
                            py: 1.5,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {startDate}
                        </TableCell>

                        {/* Start Time */}
                        <TableCell
                          sx={{
                            fontSize: '0.875rem',
                            color: '#4a4a4a',
                            py: 1.5,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {startTime}
                        </TableCell>

                        {/* End Time */}
                        <TableCell
                          sx={{
                            fontSize: '0.875rem',
                            color: '#4a4a4a',
                            py: 1.5,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {endTime}
                        </TableCell>

                        {/* Success Count */}
                        <TableCell
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#2e7d32',
                            py: 1.5,
                            textAlign: 'center',
                          }}
                        >
                          {successCount !== '' ? successCount : '-'}
                        </TableCell>

                        {/* Failed Count */}
                        <TableCell
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#d32f2f',
                            py: 1.5,
                            textAlign: 'center',
                          }}
                        >
                          {failedCount !== '' ? failedCount : '-'}
                        </TableCell>

                        {/* Logs */}
                        <TableCell sx={{ textAlign: 'center', py: 1.5 }}>
                          <Tooltip title="View Logs" placement="top">
                            <IconButton
                              onClick={() => handleViewLogs(run.workflow_run_id)}
                              size="small"
                              sx={{
                                color: '#0056D2',
                                '&:hover': {
                                  backgroundColor: '#e3f2fd',
                                },
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                        {errorMessage && (
                          <TableRow>
                            <TableCell colSpan={9} sx={{ p: 0, border: 'none' }}>
                              <Box
                                sx={{
                                  mx: 2,
                                  mb: 1,
                                  px: 2,
                                  py: 1.5,
                                  borderRadius: '8px',
                                  backgroundColor: '#ffebee',
                                  border: '1px solid #ffcdd2',
                                  color: '#d32f2f',
                                  fontWeight: 500,
                                  fontSize: '0.875rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Error sx={{ fontSize: '1rem' }} />
                                {errorMessage}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          p: 2.5,
          borderTop: '1px solid #E8E8EE',
          background: '#F9F9FB',
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            px: 4,
            py: 1,
            background: '#0056D2',
            color: '#ffffff',
            boxShadow: 'none',
            '&:hover': {
              background: '#004bb5',
              boxShadow: '0 4px 12px rgba(0, 86, 210, 0.2)',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>

      {/* Logs Dialog */}
      <LogsViewer
        open={logsDialogOpen}
        onClose={() => setLogsDialogOpen(false)}
        workflowRunId={selectedWorkflowRunId}
      />
    </Dialog>
  );
};

WorkflowRunDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default WorkflowRunDialog;
